/**
 * Formats a date (provided as a number, string, or Date object) to a localized date string.
 *
 * @param date - The date to format. Can be a timestamp (in milliseconds), an ISO 8601 date string, or a Date object.
 * @param props - Optional configuration parameters for formatting.
 * @param props.locales - The locale(s) to use for formatting (e.g., 'en-US', 'fr-FR'). Defaults to 'en-AU' (Australia).
 * @param props.options - Additional options for formatting (see Intl.DateTimeFormatOptions).
 * @returns A formatted date string in the specified locale.
 *
 * @example
 * // Using a timestamp
 * const formattedDate1 = formatDateForLocale(1682024800000); // '21/04/2023'
 *
 * // Using an ISO 8601 date string
 * const formattedDate2 = formatDateForLocale('2023-04-21'); // '21/04/2023'
 *
 * // Using a Date object
 * const formattedDate3 = formatDateForLocale(new Date('2023-04-21')); // '21/04/2023'
 *
 * // Custom locale and options
 * const formattedDate4 = formatDateForLocale(1682024800000, {
 *   locales: 'en-US',
 *   options: { year: 'numeric', month: 'long', day: 'numeric' },
 * }); // 'April 21, 2023'
 */

export const formatDateForLocale = (
  date: number | string | Date,
  props?: {
    locales?: Intl.LocalesArgument;
    options?: Intl.DateTimeFormatOptions;
  },
) =>
  new Date(date).toLocaleDateString(props?.locales || 'en-AU', props?.options);
